import { all, takeEvery, fork, put, select, call, cancelled } from 'redux-saga/effects';
import { AnyAction } from 'redux';

import { subjectsCardsActionsInterfaces, subjectsCardsActions, subjectsCardsActionTypes } from '.';
import { staticViewsActions, staticViewsActionTypes, staticViewsSelectors } from '..';
import { executeErrorCode } from '../../auth/actions';
import { LEARNING_EFFECTS_PUBLISH_IN_SID_SUCCESS } from '../../dynamicTabs/kartaPrzedmiotu/types';

import SubjectsCardsService from '../../../services/subjectsCards';

const mandatoryColumns = [
  'przedmiotId',
  'czyWersja2019',
  'czyWersja2020',
  'kierunekNazwa',
  'dataAktualizacji',
  'poziomStudiow',
  'przedmiotKod',
];
const applicableView = 'SubjectsCards';

export function* initSubjectsCardsWatcher(): Generator {
  yield takeEvery(subjectsCardsActionTypes.INIT, initSubjectsCards);
}

export function* initSubjectsCards(action: subjectsCardsActionsInterfaces.Init): Generator {
  const { view } = action.payload;
  const { loading, data, userColumnsSetup } = (yield select(state =>
    staticViewsSelectors.getStaticViewState(state, view),
  )) as ViewDataSet;
  if (!loading && !data.length) {
    try {
      /*
        czekamy, aż wykonaja się saga pobierająca config kolumn
        - kolumny pobieramy tylko, gdy userColumnsSetup jest pusty - unikamy niepotrzebnego requestu (mógł być pobrany wcześniej, jeśli została zainicjalizowana już dowolna karta przedmiotu, która korzysta z tego samego configu)
        - call - zgodnie z dokumentacją jako argumentu nie można podać obiektu dispatchowanej akcji.
          W tym przypadku podawany jest generator a kolejne argumenty to argumenty przekazywane do tego generatora (w tym przypadku saga przyjmuje obiekt akcji). Oznacza to, że nie jest dispatchowana akcja w redux.
      */
      yield all([
        !userColumnsSetup.details.length && !userColumnsSetup.main.length
          ? put(subjectsCardsActions.getColumnsConfig(view))
          : null,
      ]);
      yield put(subjectsCardsActions.getData(view));
      yield put(staticViewsActions.initFinish(view));
    } catch (error) {
      if (error && error.errorCode) {
        yield put(executeErrorCode(error.errorCode, action, error));
      }
    }
  }
}
export function* getSubjectsCardsWatcher(): Generator {
  yield takeEvery(subjectsCardsActionTypes.GET_DATA_START, getSubjectsCards);
}
/**
 * pobieranie danych do tabeli "Karty przedmiotów"
 *
 * @export
 * @param {AnyAction} action
 * @return {*}  {Generator}
 */
export function* getSubjectsCards(action: subjectsCardsActionsInterfaces.GetData): Generator {
  const { view } = action.payload;
  const abortController = new AbortController();
  try {
    const { search, sorting, userColumnsSetup, page, isWKD } = (yield select(state =>
      staticViewsSelectors.getStaticViewState(state, view),
    )) as ViewDataSet;
    const columns = [...userColumnsSetup.main];
    mandatoryColumns.forEach(key => {
      if (!columns.includes(key)) {
        columns.push(key);
      }
    });
    const requestAtributes = {
      filter: [{ name: 'isWkd', values: [isWKD.toString()] }],
      page: page.toString(),
      columns,
      sort: sorting.order ? (sorting.order === 'ascend' ? 'asc' : 'desc') : null,
      orderBy: sorting.columnName,
      search,
    };
    const response: any = yield call(SubjectsCardsService.getSubjectsCards, requestAtributes, abortController.signal);
    /* Aby poprawnie zmapować dane w reducer, payload powinien być zbudowany wg interfejsu GetDataSuccessPayload */
    const payload: GetDataSuccessPayload = {
      ...response.kartaPrezdmiotuListaElementPageDto,
      cacheDuration: response.cacheTime,
      controlSum: response.controlSum,
      data: response.kartaPrezdmiotuListaElementPageDto.data.map((obj: any) => ({
        ...obj,
        key: obj.przedmiotId,
      })),
    };
    yield put(staticViewsActions.getDataSuccess(view, payload));
  } catch (error) {
    if (error && error.errorCode) {
      yield put(executeErrorCode(error.errorCode, action, error));
    }
    yield put(staticViewsActions.getDataFail(view));
  } finally {
    if (yield cancelled()) {
      abortController.abort();
    }
  }
}

export function* getSubjectsCardsNextPageWatcher(): Generator {
  yield takeEvery(subjectsCardsActionTypes.GET_DATA_NEXT_PAGE_START, getSubjectsCardsNextPage);
}
/**
 * dobieranie kolejnej partii danych do tabeli "Karty przedmiotów"
 *
 * @export
 * @param {AnyAction} action
 * @return {*}  {Generator}
 */
export function* getSubjectsCardsNextPage(action: subjectsCardsActionsInterfaces.GetDataNextPage): Generator {
  const { view } = action.payload;
  try {
    const { userColumnsSetup, sorting, page, search, isWKD } = (yield select(state =>
      staticViewsSelectors.getStaticViewState(state, view),
    )) as ViewDataSet;
    const columns = [...userColumnsSetup.main];
    mandatoryColumns.forEach(key => {
      if (!columns.includes(key)) {
        columns.push(key);
      }
    });
    const requestAtributes = {
      filter: [{ name: 'isWkd', values: [isWKD.toString()] }],
      page: page + 1,
      columns,
      sort: sorting.order ? (sorting.order === 'ascend' ? 'asc' : 'desc') : null,
      orderBy: sorting.columnName,
      search,
    };
    const response: any = yield call(SubjectsCardsService.getSubjectsCards, requestAtributes);
    /* Aby poprawnie zmapować dane w reducer, payload powinien być zbudowany wg interfejsu GetDataSuccessPayload */
    const payload: GetDataSuccessPayload = {
      ...response.kartaPrezdmiotuListaElementPageDto,
      cacheDuration: response.cacheTime,
      controlSum: response.controlSum,
      data: response.kartaPrezdmiotuListaElementPageDto.data.map((obj: any) => ({
        ...obj,
        key: obj.przedmiotId,
      })),
    };
    yield put(staticViewsActions.getDataNextPageSuccess(view, payload));
    yield put(staticViewsActions.setPage(action.payload.view, page + 1));
    // }
  } catch (error) {
    if (error && error.errorCode) {
      yield put(executeErrorCode(error.errorCode, action, error));
    }
    yield put(staticViewsActions.getDataNextPageFail(view));
  }
}

export function* saveColumnsUserSetupWatcher(): Generator {
  yield takeEvery(subjectsCardsActionTypes.COLUMNS_SAVE_USER_SETUP_START, saveColumnsUserSetup);
}
/**
 * zapisanie konfiguracji kolumn użytkownika dla tabeli "Karty przedmiotów"
 *
 * @export
 * @param {AnyAction} action
 * @return {*}  {Generator}
 */
export function* saveColumnsUserSetup(action: subjectsCardsActionsInterfaces.SaveColumnsUserSetup): Generator {
  const { view, selectedColumns } = action.payload;
  try {
    yield call(SubjectsCardsService.saveColumnsUserSetup, { columnsSetup: selectedColumns });
    yield put(subjectsCardsActions.getColumnsConfig(view));
  } catch (error) {
    if (error && error.errorCode) {
      yield put(executeErrorCode(error.errorCode, action, error));
    }
  }
}

export function* getColumnsConfigWatcher(): Generator {
  yield takeEvery(subjectsCardsActionTypes.COLUMNS_GET_CONFIG_START, getColumnsConfig);
}
/**
 * pobieranie konfiguracji kolumn dla tabeli "Karty przedmiotów".
 *
 * @export
 * @param {AnyAction} action
 * @return {*}  {Generator}
 */
export function* getColumnsConfig(action: subjectsCardsActionsInterfaces.GetColumnsConfig): Generator {
  const { view } = action.payload;
  try {
    const response: any = yield call(SubjectsCardsService.getSubjectsCardsColumnsData);
    yield put(staticViewsActions.saveColumnsUserSetupSuccess(view, response.kartaPrzedmiotuField));
    yield put(staticViewsActions.getColumnsConfigSuccess(view, response.kartaPrzedmiotuField));
  } catch (error) {
    if (error && error.errorCode) {
      yield put(executeErrorCode(error.errorCode, action, error));
    }
    yield put(staticViewsActions.getColumnsConfigFail(view));
  }
}

export function* refreshSubjectsCardsDataWatcher(): Generator {
  yield takeEvery(
    [
      staticViewsActionTypes.SET_ORDER,
      LEARNING_EFFECTS_PUBLISH_IN_SID_SUCCESS,
      staticViewsActionTypes.COLUMNS_GET_CONFIG_SUCCESS,
      subjectsCardsActionTypes.REFRESH_DATA_START,
    ],
    refreshSubjectsCardsData,
  );
}
/**
 * odświeżenie listy
 *
 * @export
 * @return {*}  {Generator}
 *
 */
export function* refreshSubjectsCardsData(action: subjectsCardsActionsInterfaces.RefreshData): Generator {
  const { view } = action.payload;
  const { initTable, selected } = (yield select(state => staticViewsSelectors.getStaticViewState(state, view))) as ViewDataSet;
  if (view === applicableView && !initTable) {
    yield put(staticViewsActions.setLoading(action.payload.view));
    yield call(getSubjectsCards, subjectsCardsActions.getData(action.payload.view));
    if (selected.length === 1) {
      yield put(subjectsCardsActions.getRecordDetails(view, selected[0]));
    }
  }
}

export function* getSubjectsCardsGetDetailsWatcher(): Generator {
  yield takeEvery(subjectsCardsActionTypes.RECORD_GET_DETAILS_START, getSubjectsCardsGetDetails);
}
/**
 * pobieranie szczegółów wybranego rekordu
 *
 * @export
 * @param {*} action
 * @return {*}  {Generator}
 */
export function* getSubjectsCardsGetDetails(action: subjectsCardsActionsInterfaces.GetRecordDetails): Generator {
  const { view, id } = action.payload;
  try {
    const { data, userColumnsSetup } = (yield select(state =>
      staticViewsSelectors.getStaticViewState(state, view),
    )) as ViewDataSet;
    const selectedRow = data.find((row: any) => row.przedmiotId === id);
    if ((selectedRow && !selectedRow.details) || (selectedRow && Object.getOwnPropertyNames(selectedRow.details).length === 0)) {
      const columns = [...userColumnsSetup.details];
      mandatoryColumns.forEach(key => {
        if (!columns.includes(key)) {
          columns.push(key);
        }
      });
      const requestParams = {
        filter: {
          przedmiotId: id,
        },
        columns,
      };
      const response: any = yield call(SubjectsCardsService.getSubjectCardDetails, requestParams);
      yield put(staticViewsActions.getRecordDetailsSuccess(view, response.kartaPrezdmiotuListaElementPageDto.data[0], id));
    } else {
      yield put(staticViewsActions.setDetailsLoading(view));
    }
  } catch (error) {
    if (error && error.errorCode) {
      yield put(executeErrorCode(error.errorCode, action, error));
    }
    yield put(staticViewsActions.getRecordDetailsFail(view));
  }
}

export function* reloadSubjectsCardsDataWatcher(): Generator {
  yield takeEvery(subjectsCardsActionTypes.CHANGE_WKD_FILTER, reloadSubjectsCardsData);
}
/**
 *  funkcja odświeżająca listę kart przedmiotów po akcji zmiany filtra WKD
 *
 * @export
 * @param {AnyAction} action
 * @return {*}  {Generator}
 */
export function* reloadSubjectsCardsData(action: AnyAction): Generator {
  try {
    yield put(subjectsCardsActions.getData(applicableView));
  } catch (error) {
    if (error && error.errorCode) {
      yield put(executeErrorCode(error.errorCode, action, error));
    }
  }
}

export default function* rootSaga(): Generator {
  yield all([
    fork(initSubjectsCardsWatcher),
    fork(getSubjectsCardsWatcher),
    fork(getSubjectsCardsNextPageWatcher),
    fork(refreshSubjectsCardsDataWatcher),
    fork(saveColumnsUserSetupWatcher),
    fork(getSubjectsCardsGetDetailsWatcher),
    fork(reloadSubjectsCardsDataWatcher),
    fork(getColumnsConfigWatcher),
  ]);
}
